import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  IDSButton, IDSInput, IDSSelect, IDSAccordion, IDSTag, IDSCheckButton,
} from '@inera/ids-react';
import ConfirmDialog from '../ConfirmDialog';
import StatusAlert from '../StatusAlert';
import {
  fetchServiceOrderGroups,
  updateServiceOrderGroup,
  deleteServiceOrderGroup,
  fetchSourceSystemOptions,
} from '../../services/serviceOrderAPI';
import { fetchContracts } from '../../services/contractsAPI';
import { fetchOrganisations } from '../../services/adminAPI';
import { fetchTestersByOrganisation } from '../../services/usersAPI';
import { formatDateTimeNoSeconds } from '../../utils/dateTime';

const contractLabel = (contract) =>
  `${contract.contractName}${contract.version ? ` ${contract.version}` : ''}`;

const groupKey = (group) => `${group.serviceNowId}|${group.organisationId}`;

// Matches a stored source system string ("Melior v1") back onto the Jira option tree
const matchSourceSystem = (options, value) => {
  if (!value) return { parentId: '', childId: '' };
  const parent = options.find(option => value === option.parentLabel || value.startsWith(`${option.parentLabel} `));
  if (!parent) return { parentId: '', childId: '' };
  const child = (parent.children || []).find(c => `${parent.parentLabel} ${c.label}` === value);
  return { parentId: parent.parentId, childId: child ? child.id : '' };
};

// The combinations that can no longer be removed because the tester already started testing
const startedCombinations = (group) =>
  (group.orders || [])
    .filter(order => !!order.testExecutionId)
    .map(order => ({ testerEmail: order.testerEmail, contractId: order.contractId }));

// ── Edit form for a complete order (testers × contracts) ─────────────────────
const ServiceOrderGroupForm = ({ group, contracts, sourceSystemOptions, onSave, onCancel }) => {
  const [serviceNowId, setServiceNowId] = useState(group.serviceNowId || '');
  const [serviceProvider, setServiceProvider] = useState(group.serviceProvider || '');
  const [selectedTesterEmails, setSelectedTesterEmails] = useState(() => new Set(group.testerEmails || []));
  const [selectedContractIds, setSelectedContractIds] = useState(
    () => new Set((group.contracts || []).map(contract => contract.id))
  );
  const initialSource = useMemo(
    () => matchSourceSystem(sourceSystemOptions, group.sourceSystem),
    [sourceSystemOptions, group.sourceSystem]
  );
  const [sourceParentId, setSourceParentId] = useState(initialSource.parentId);
  const [sourceChildId, setSourceChildId] = useState(initialSource.childId);
  const [testers, setTesters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    setSourceParentId(initialSource.parentId);
    setSourceChildId(initialSource.childId);
  }, [initialSource]);

  useEffect(() => {
    // Orders placed for an external testing organisation (VavTP) are staffed by testers
    // from that organisation, just like when the order was created.
    const testerOrgId = group.testingOrganisationId || group.organisationId;
    if (!testerOrgId) return;
    fetchTestersByOrganisation(testerOrgId)
      .then(setTesters)
      .catch(() => setTesters([]));
  }, [group.organisationId, group.testingOrganisationId]);

  const locked = useMemo(() => startedCombinations(group), [group]);
  const lockedTesters = useMemo(
    () => new Set(locked.map(combination => combination.testerEmail)),
    [locked]
  );
  const lockedContractIds = useMemo(
    () => new Set(locked.map(combination => combination.contractId)),
    [locked]
  );

  const selectedParent = sourceSystemOptions.find(option => option.parentId === sourceParentId);
  const selectedChild = selectedParent?.children?.find(child => child.id === sourceChildId);
  const sourceSystemValue = selectedChild
    ? `${selectedParent.parentLabel} ${selectedChild.label}`
    : selectedParent?.parentLabel || '';

  // The order's current source system is kept when it can't be matched to a Jira option,
  // so an edit of other fields never silently clears it.
  const effectiveSourceSystem = sourceSystemValue || group.sourceSystem || '';

  // Testers and contracts that testing has started for stay selected
  const toggleTester = (email) => {
    if (lockedTesters.has(email)) return;
    setSelectedTesterEmails(prev => {
      const next = new Set(prev);
      if (next.has(email)) { next.delete(email); } else { next.add(email); }
      return next;
    });
  };

  const toggleContract = (contractId) => {
    if (lockedContractIds.has(contractId)) return;
    setSelectedContractIds(prev => {
      const next = new Set(prev);
      if (next.has(contractId)) { next.delete(contractId); } else { next.add(contractId); }
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = {};
    if (!serviceNowId.trim()) errors.serviceNowId = 'ServiceNow ärendenummer krävs.';
    if (!effectiveSourceSystem) errors.sourceSystem = 'Välj ett källsystem.';
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    if (selectedTesterEmails.size === 0) {
      setError('Välj minst en testare.');
      return;
    }
    if (selectedContractIds.size === 0) {
      setError('Välj minst ett tjänstekontrakt.');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      await onSave({
        serviceNowId: serviceNowId.trim(),
        sourceSystem: effectiveSourceSystem,
        serviceProvider: serviceProvider.trim(),
        testerEmails: Array.from(selectedTesterEmails),
        contractIds: Array.from(selectedContractIds),
      });
    } catch (err) {
      setError(err.message || 'Något gick fel. Försök igen.');
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      noValidate
      style={{
        background: 'var(--ids-palette-neutral-95)',
        border: '1px solid var(--ids-palette-neutral-90)',
        borderRadius: '6px',
        padding: '1.25rem',
        marginBottom: '1rem',
      }}
    >
      <div className="ids-mb-3">
        <IDSInput
          label="ServiceNow ärendenummer *"
          type="text"
          value={serviceNowId}
          invalid={!!fieldErrors.serviceNowId}
          errorMsg={fieldErrors.serviceNowId}
          onChange={e => { setServiceNowId(e.target.value); setFieldErrors(prev => ({ ...prev, serviceNowId: null })); }}
        />
      </div>

      <div className="ids-mb-3">
        <p className="ids-label" style={{ marginBottom: '0.5rem', fontWeight: 500 }}>
          Testare * <span style={{ fontWeight: 400, color: 'var(--ids-palette-neutral-60)', fontSize: '0.875rem' }}>
            ({selectedTesterEmails.size} valda)
          </span>
        </p>
        {testers.length === 0 && selectedTesterEmails.size === 0 ? (
          <p style={{ color: 'var(--ids-palette-neutral-70)', fontSize: '0.875rem' }}>Inga testare hittades för organisationen.</p>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
            {/* Testers that are no longer members of the organisation are still shown so they can be removed */}
            {[...new Set([...testers.map(t => t.email), ...(group.testerEmails || [])])].map(email => {
              const tester = testers.find(t => t.email === email);
              const isLocked = lockedTesters.has(email);
              return (
                <IDSCheckButton
                  key={`${email}-${selectedTesterEmails.has(email)}`}
                  defaultChecked={selectedTesterEmails.has(email)}
                  disabled={isLocked}
                  onChange={() => toggleTester(email)}
                >
                  {tester ? tester.username : email}
                  <span style={{ color: 'var(--ids-palette-neutral-60)', fontSize: '0.8rem' }}>
                    {tester ? ` ${email}` : ''}{isLocked ? ' (testning påbörjad)' : ''}
                  </span>
                </IDSCheckButton>
              );
            })}
          </div>
        )}
      </div>

      <div className="ids-mb-3">
        <p className="ids-label" style={{ marginBottom: '0.5rem', fontWeight: 500 }}>
          Tjänstekontrakt * <span style={{ fontWeight: 400, color: 'var(--ids-palette-neutral-60)', fontSize: '0.875rem' }}>
            ({selectedContractIds.size} valda)
          </span>
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
          {contracts.map(contract => {
            const isLocked = lockedContractIds.has(contract.id);
            return (
              <IDSCheckButton
                key={`${contract.id}-${selectedContractIds.has(contract.id)}`}
                defaultChecked={selectedContractIds.has(contract.id)}
                disabled={isLocked}
                onChange={() => toggleContract(contract.id)}
              >
                {contractLabel(contract)}
                {isLocked && (
                  <span style={{ marginLeft: '0.4rem', fontSize: '0.75rem', color: 'var(--ids-palette-neutral-60)' }}>
                    (testning påbörjad)
                  </span>
                )}
              </IDSCheckButton>
            );
          })}
        </div>
      </div>

      <div className="ids-mb-3">
        <IDSSelect
          id="service-order-source-system"
          label="Källsystem *"
          value={sourceParentId}
          invalid={!!fieldErrors.sourceSystem}
          errorMsg={fieldErrors.sourceSystem}
          onChange={e => {
            setSourceParentId(e.target.value);
            setSourceChildId('');
            setFieldErrors(prev => ({ ...prev, sourceSystem: null }));
          }}
        >
          <option value="">{group.sourceSystem ? `Behåll: ${group.sourceSystem}` : 'Välj källsystem'}</option>
          {sourceSystemOptions.map(option => (
            <option key={option.parentId} value={option.parentId}>{option.parentLabel}</option>
          ))}
        </IDSSelect>
      </div>

      {selectedParent?.children?.length > 0 && (
        <div className="ids-mb-3">
          <IDSSelect
            id="service-order-source-system-child"
            label="Källsystem version/steg"
            value={sourceChildId}
            onChange={e => setSourceChildId(e.target.value)}
          >
            <option value="">Ingen undernivå</option>
            {selectedParent.children.map(child => (
              <option key={child.id} value={child.id}>{child.label}</option>
            ))}
          </IDSSelect>
        </div>
      )}

      <div className="ids-mb-3">
        <IDSInput
          label="Tjänsteproducent"
          type="text"
          value={serviceProvider}
          placeholder="t.ex. RTJP: SKLTP, VP-Services v 4.3.1"
          onChange={e => setServiceProvider(e.target.value)}
        />
      </div>

      <p style={{ fontSize: '0.8rem', color: 'var(--ids-palette-neutral-50)', marginBottom: '1rem' }}>
        Nya kombinationer av testare och tjänstekontrakt skapas när du sparar. Ändrade uppgifter uppdateras
        endast i Mimir — befintliga AO/TPV-ärenden i Jira måste uppdateras manuellt.
      </p>

      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <IDSButton size="s" type="submit" disabled={loading}>{loading ? 'Sparar...' : 'Spara beställning'}</IDSButton>
        <IDSButton size="s" secondary type="button" onClick={onCancel}>Avbryt</IDSButton>
      </div>

      <StatusAlert error={error} className="ids-mt-3" />
    </form>
  );
};

// ── One complete order as an expandable row ──────────────────────────────────
const ServiceOrderGroupAccordion = ({ group, contracts, sourceSystemOptions, onUpdated, onDeleted }) => {
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState(null);

  const started = group.testingStarted;

  const handleSave = async (data) => {
    const updated = await updateServiceOrderGroup({
      organisationId: group.organisationId,
      originalServiceNowId: group.serviceNowId,
      ...data,
    });
    onUpdated(groupKey(group), updated);
    setEditing(false);
  };

  const doDelete = async () => {
    setConfirmDelete(false);
    setDeleting(true);
    setError(null);
    try {
      await deleteServiceOrderGroup(group.serviceNowId, group.organisationId);
      onDeleted(groupKey(group));
    } catch (err) {
      setError(err.message || 'Något gick fel. Försök igen.');
      setDeleting(false);
    }
  };

  return (
    <>
      {confirmDelete && (
        <ConfirmDialog
          message={`Vill du ta bort beställningen "${group.serviceNowId}" med ${group.testerEmails.length} testare och ${group.contracts.length} tjänstekontrakt?`}
          onConfirm={doDelete}
          onCancel={() => setConfirmDelete(false)}
        />
      )}
      <IDSAccordion
        headline={
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <h3>{group.serviceNowId}</h3>
            <span style={{ fontSize: '0.85rem', color: 'var(--ids-palette-neutral-60)' }}>
              {group.organisationName}
              {` · ${group.testerEmails.length} testare · ${group.contracts.length} tjänstekontrakt`}
            </span>
            {group.testingOrganisationName && <IDSTag>VavTP</IDSTag>}
            {started && <IDSTag>Testning påbörjad</IDSTag>}
          </div>
        }
        headlineSize="m"
        level={1}
      >
        <div>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
            <IDSButton size="s" type="button" onClick={() => setEditing(e => !e)}>
              {editing ? 'Avbryt redigering' : 'Redigera beställning'}
            </IDSButton>
            <IDSButton
              size="s"
              type="button"
              disabled={deleting || started}
              title={started ? 'Beställningen kan inte tas bort eftersom testning redan har påbörjats.' : undefined}
              onClick={() => setConfirmDelete(true)}
            >
              {deleting ? '...' : 'Ta bort beställning'}
            </IDSButton>
          </div>

          <StatusAlert error={error} className="ids-mb-3" />

          {editing && (
            <ServiceOrderGroupForm
              group={group}
              contracts={contracts}
              sourceSystemOptions={sourceSystemOptions}
              onSave={handleSave}
              onCancel={() => setEditing(false)}
            />
          )}

          <dl style={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '0.35rem 1rem', margin: '0 0 1.25rem' }}>
            <dt style={{ fontWeight: 600 }}>Organisation</dt>
            <dd style={{ margin: 0 }}>{group.organisationName || '–'}</dd>
            <dt style={{ fontWeight: 600 }}>Testorganisation</dt>
            <dd style={{ margin: 0 }}>{group.testingOrganisationName || '–'}</dd>
            <dt style={{ fontWeight: 600 }}>Källsystem</dt>
            <dd style={{ margin: 0 }}>{group.sourceSystem || '–'}</dd>
            <dt style={{ fontWeight: 600 }}>Tjänsteproducent</dt>
            <dd style={{ margin: 0 }}>{group.serviceProvider || '–'}</dd>
            <dt style={{ fontWeight: 600 }}>TPV</dt>
            <dd style={{ margin: 0 }}>
              {group.tpvIssueKey
                ? (group.tpvIssueLink
                    ? <a href={group.tpvIssueLink} target="_blank" rel="noopener noreferrer">{group.tpvIssueKey}</a>
                    : group.tpvIssueKey)
                : '–'}
            </dd>
            <dt style={{ fontWeight: 600 }}>Skapad</dt>
            <dd style={{ margin: 0 }}>{formatDateTimeNoSeconds(group.createdAt, '–')}</dd>
          </dl>

          <h4 className="ids-heading-s ids-mb-2">Testare</h4>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem', marginBottom: '1.25rem' }}>
            {group.testerEmails.map(email => <IDSTag key={email}>{email}</IDSTag>)}
          </div>

          <h4 className="ids-heading-s ids-mb-2">Tjänstekontrakt</h4>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem', marginBottom: '1.25rem' }}>
            {group.contracts.map(contract => <IDSTag key={contract.id}>{contractLabel(contract)}</IDSTag>)}
          </div>

          <h4 className="ids-heading-s ids-mb-2">Tilldelningar</h4>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--ids-palette-neutral-90)' }}>
                <th style={{ padding: '0.4rem 0.5rem' }}>Testare</th>
                <th style={{ padding: '0.4rem 0.5rem' }}>Tjänstekontrakt</th>
                <th style={{ padding: '0.4rem 0.5rem' }}>AO</th>
                <th style={{ padding: '0.4rem 0.5rem' }}>Teststatus</th>
              </tr>
            </thead>
            <tbody>
              {group.orders.map(order => (
                <tr key={order.id} style={{ borderBottom: '1px solid var(--ids-palette-neutral-95)' }}>
                  <td style={{ padding: '0.4rem 0.5rem' }}>{order.testerEmail}</td>
                  <td style={{ padding: '0.4rem 0.5rem' }}>
                    {order.contractName}{order.contractVersion ? ` ${order.contractVersion}` : ''}
                  </td>
                  <td style={{ padding: '0.4rem 0.5rem' }}>
                    {order.aoIssueKey
                      ? (order.aoIssueLink
                          ? <a href={order.aoIssueLink} target="_blank" rel="noopener noreferrer">{order.aoIssueKey}</a>
                          : order.aoIssueKey)
                      : '–'}
                  </td>
                  <td style={{ padding: '0.4rem 0.5rem' }}>{order.testExecutionStatus || 'Ej påbörjad'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </IDSAccordion>
    </>
  );
};

// ── Main layout ──────────────────────────────────────────────────────────────
const ServiceOrdersAdminLayout = () => {
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [organisations, setOrganisations] = useState([]);
  const [sourceSystemOptions, setSourceSystemOptions] = useState([]);
  const [organisationFilter, setOrganisationFilter] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [groupsData, contractsData, orgsData] = await Promise.all([
          fetchServiceOrderGroups(),
          fetchContracts(),
          fetchOrganisations(),
        ]);
        setGroups(groupsData);
        setContracts(Array.isArray(contractsData) ? contractsData : []);
        setOrganisations(Array.isArray(orgsData) ? orgsData : []);
      } catch (err) {
        setError(err.message || 'Något gick fel. Försök igen.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Source systems come from Jira and are only needed when editing, so a failure here
  // must not block the list itself.
  useEffect(() => {
    fetchSourceSystemOptions()
      .then(setSourceSystemOptions)
      .catch(() => setSourceSystemOptions([]));
  }, []);

  const handleUpdated = (key, updated) => {
    setGroups(prev => prev.map(group => (groupKey(group) === key ? updated : group)));
  };

  const handleDeleted = (key) => {
    setGroups(prev => prev.filter(group => groupKey(group) !== key));
  };

  const visibleGroups = useMemo(() => {
    const term = search.trim().toLowerCase();
    return groups.filter(group => {
      if (organisationFilter && group.organisationId !== organisationFilter) return false;
      if (!term) return true;
      const haystack = [
        group.serviceNowId,
        group.organisationName,
        group.tpvIssueKey,
        ...(group.testerEmails || []),
        ...(group.contracts || []).map(contract => contract.contractName),
      ].filter(Boolean);
      return haystack.some(field => field.toLowerCase().includes(term));
    });
  }, [groups, organisationFilter, search]);

  return (
    <main className="ids-content ids-p-6" style={{ maxWidth: '900px', margin: '2rem auto' }}>
      <h1 className="ids-heading-l ids-mb-2">Beställningar</h1>

      <section className="ids-mt-6">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h2 className="ids-heading-m">Alla beställningar</h2>
          <IDSButton size="s" type="button" onClick={() => navigate('/createserviceorder')}>+ Skapa ny beställning</IDSButton>
        </div>

        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
          <div style={{ minWidth: '240px' }}>
            <IDSSelect
              id="service-order-organisation-filter"
              label="Filtrera på organisation"
              value={organisationFilter}
              onChange={e => setOrganisationFilter(e.target.value)}
            >
              <option value="">Alla organisationer</option>
              {organisations.map(org => (
                <option key={org.id} value={org.id}>{org.name}</option>
              ))}
            </IDSSelect>
          </div>
          <div style={{ minWidth: '240px' }}>
            <IDSInput
              label="Sök"
              type="text"
              value={search}
              placeholder="Ärendenummer, testare, kontrakt..."
              onChange={e => setSearch(e.target.value)}
            />
          </div>
        </div>

        {loading && <p>Laddar...</p>}
        <StatusAlert error={error} />
        {!loading && !error && visibleGroups.length === 0 && (
          <p style={{ color: 'var(--ids-palette-neutral-60)' }}>Inga beställningar hittades.</p>
        )}

        {visibleGroups.map(group => (
          <ServiceOrderGroupAccordion
            key={groupKey(group)}
            group={group}
            contracts={contracts}
            sourceSystemOptions={sourceSystemOptions}
            onUpdated={handleUpdated}
            onDeleted={handleDeleted}
          />
        ))}
      </section>
    </main>
  );
};

export default ServiceOrdersAdminLayout;
